import { Field, FieldProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedNumber } from 'react-intl';
import { Grid, Theme } from '@mui/material';
import { PoolTextField, PoolTypography } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { ConverterCustomerSupplier } from '../models/internal/converter-customer-supplier';
import { ConverterJob } from '../models/internal/converterJob';
import { FinancialCalculations } from '../../../common/utils/financial-calculations';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            paddingRight: theme.spacing(3),
        },
        delivery: {
            '& > div > input': {
                textAlign: 'right',
                paddingRight: 5,
            },
        },
        summaryLeft: {
            padding: theme.spacing(0, 3),
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        summaryRight: {
            padding: theme.spacing(2, 0),
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: theme.spacing(7),
        },
        summaryHighlight: {
            backgroundColor: '#F1F1F1',
        },
        padRight: {
            marginRight: 20,
        },
    });

interface ConvertReceiptCustomerSupplierFooterProps extends WithStyles<typeof styles> {
    converterSupplier: ConverterCustomerSupplier;
    converterCustomerIndex: number;
    converterSupplierIndex: number;
    currency: string;
    disabled: boolean;
    showDeliveryCosts: boolean;
}
export const ConvertReceiptCustomerSupplierFooter = withStyles(styles)(
    class ConvertReceiptCustomerSupplierFooterBase extends FormComponent<
        ConverterJob,
        ConvertReceiptCustomerSupplierFooterProps
    > {
        public render() {
            const {
                classes,
                converterSupplier,
                converterSupplierIndex,
                converterCustomerIndex,
                currency,
                showDeliveryCosts,
            } = this.props;

            const someSelected = converterSupplier.items.some(item => !!item.isSelected);
            let total = 0;
            let taxTotal = 0;

            if (someSelected) {
                const netTotal = converterSupplier.items.reduce(
                    (sum, item) => (item.isSelected ? (sum += FinancialCalculations.getNetTotal(item)) : sum),
                    0
                );
                taxTotal = converterSupplier.items.reduce(
                    (sum, item) => (item.isSelected ? (sum += FinancialCalculations.getTax(item)) : sum),
                    0
                );
                total =
                    netTotal + taxTotal + (converterSupplier.deliveryPrice || 0) + (converterSupplier.surcharge || 0);
            }

            const fieldPrefix = `customers.${converterCustomerIndex}.customerSuppliers.${converterSupplierIndex}.`;

            return (
                <div className={classes.container}>
                    <Grid container={true} spacing={2} direction="column" alignItems="flex-end">
                        <Grid item={true} container={true} xs={12} lg={3} direction="row">
                            {showDeliveryCosts && (
                                <>
                                    <Grid item={true} xs={7} className={classes.summaryLeft}>
                                        <PoolTypography id="app.label.deliverCosts" />
                                    </Grid>
                                    <Grid item={true} xs={5} className={classes.summaryRight}>
                                        <Field name={`${fieldPrefix}deliveryPrice`}>
                                            {({ field, form }: FieldProps<ConverterJob>) => (
                                                <PoolTextField
                                                    className={classes.delivery}
                                                    fieldType="fullWidth"
                                                    isNumeric={true}
                                                    minDecimalScale={2}
                                                    {...field}
                                                    error={this.hasError(form, field.name)}
                                                    helperText={this.getError(form, field.name)}
                                                    disabled={this.props.disabled}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </>
                            )}
                            {showDeliveryCosts && (
                                <>
                                    <Grid item={true} xs={7} className={classes.summaryLeft}>
                                        <PoolTypography id="app.label.surcharges" />
                                    </Grid>
                                    <Grid item={true} xs={5} className={classes.summaryRight}>
                                        <Field name={`${fieldPrefix}surcharge`}>
                                            {({ field, form }: FieldProps<ConverterJob>) => (
                                                <PoolTextField
                                                    className={classes.delivery}
                                                    fieldType="fullWidth"
                                                    isNumeric={true}
                                                    minDecimalScale={2}
                                                    {...field}
                                                    error={this.hasError(form, field.name)}
                                                    helperText={this.getError(form, field.name)}
                                                    disabled={this.props.disabled}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                </>
                            )}

                            <Grid item={true} xs={6} className={classes.summaryLeft}>
                                <PoolTypography id="app.label.tax" />
                            </Grid>
                            <Grid item={true} xs={6} className={classes.summaryRight}>
                                <PoolTypography className={classes.padRight}>
                                    <FormattedNumber value={taxTotal} currency={currency} style="currency" />
                                </PoolTypography>
                            </Grid>

                            <Grid item={true} xs={6} className={`${classes.summaryLeft} ${classes.summaryHighlight}`}>
                                <PoolTypography id="app.label.total" />
                            </Grid>
                            <Grid item={true} xs={6} className={`${classes.summaryRight} ${classes.summaryHighlight}`}>
                                <PoolTypography className={classes.padRight}>
                                    <FormattedNumber value={total} currency={currency} style="currency" />
                                </PoolTypography>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            );
        }
    }
);
